import { useCallback, useState, type ReactNode } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
  DragOverlay as DndKitDragOverlay,
} from "@dnd-kit/core";

// Workaround for React 19 type compatibility with @dnd-kit
const DragOverlay = DndKitDragOverlay as unknown as React.FC<{
  children?: ReactNode;
}>;
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Volume2,
  Plus,
  MoreVertical,
  Trash2,
  GripVertical,
  Sparkles,
  ChevronDown,
} from "lucide-react";
import { Button } from "@learn-me/ui";
import type { ChapterSection } from "../../api/adminApi";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@learn-me/ui";

interface SectionListPanelProps {
  sections: ChapterSection[];
  currentIndex: number;
  onSectionClick: (index: number) => void;
  onAddSection: (options?: { withAI?: boolean }) => void;
  isAddingSection?: boolean;
  onDeleteSection: (sectionId: string) => void;
  onReorderSections?: (fromIndex: number, toIndex: number) => Promise<void>;
  isCollapsed?: boolean;
}

interface SortableSectionItemProps {
  section: ChapterSection;
  index: number;
  isActive: boolean;
  onSectionClick: () => void;
  onDeleteSection: () => void;
}

function SortableSectionItem({
  section,
  index,
  isActive,
  onSectionClick,
  onDeleteSection,
}: SortableSectionItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const hasAudio = !!section.audioUrl;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group relative flex items-start gap-2 p-3 rounded-lg cursor-pointer transition-colors mb-1 ${
        isActive
          ? "bg-primary-50 border border-primary-200"
          : "hover:bg-white border border-transparent"
      }`}
      onClick={onSectionClick}
    >
      {/* Drag Handle */}
      <button
        type="button"
        className="p-1 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="h-4 w-4" />
      </button>

      {/* Number Badge */}
      <div
        className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-medium ${
          isActive ? "bg-primary-500 text-white" : "bg-gray-200 text-gray-600"
        }`}
      >
        {index + 1}
      </div>

      {/* Section Info */}
      <div className="flex-1 min-w-0">
        <p
          className={`text-sm font-medium truncate ${
            isActive ? "text-primary-900" : "text-gray-900"
          }`}
        >
          {section.title}
        </p>

        {/* Badges */}
        <div className="flex items-center gap-2 mt-1">
          {hasAudio && (
            <span className="inline-flex items-center gap-1 text-xs text-gray-500">
              <Volume2 className="h-3 w-3" />
              Audio
            </span>
          )}
        </div>
      </div>

      {/* Context Menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            onClick={(e) => e.stopPropagation()}
            className="opacity-0 group-hover:opacity-100 p-1 hover:bg-gray-200 rounded transition-opacity"
          >
            <MoreVertical className="h-4 w-4 text-gray-500" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={(e) => {
              e.stopPropagation();
              onDeleteSection();
            }}
            className="text-red-600"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Eliminar
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// Overlay item for drag preview
function DragOverlaySectionItem({
  section,
  index,
}: {
  section: ChapterSection;
  index: number;
}) {
  const hasAudio = !!section.audioUrl;

  return (
    <div className="flex items-start gap-2 p-3 rounded-lg bg-white shadow-xl border-2 border-primary-400 w-64">
      <div className="p-1 text-gray-600">
        <GripVertical className="h-4 w-4" />
      </div>
      <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-medium bg-primary-500 text-white">
        {index + 1}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate text-primary-900">
          {section.title}
        </p>
        <div className="flex items-center gap-2 mt-1">
          {hasAudio && (
            <span className="inline-flex items-center gap-1 text-xs text-gray-500">
              <Volume2 className="h-3 w-3" />
              Audio
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Panel lateral con lista de secciones del capítulo.
 * Soporta drag & drop para reordenar secciones.
 */
export function SectionListPanel({
  sections,
  currentIndex,
  onSectionClick,
  onAddSection,
  onDeleteSection,
  onReorderSections,
  isCollapsed = false,
  isAddingSection = false,
}: SectionListPanelProps) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isReordering, setIsReordering] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  }, []);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveId(null);

      if (!over || active.id === over.id || !onReorderSections) {
        return;
      }

      const oldIndex = sections.findIndex((s) => s.id === active.id);
      const newIndex = sections.findIndex((s) => s.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        setIsReordering(true);
        try {
          await onReorderSections(oldIndex, newIndex);
        } finally {
          setIsReordering(false);
        }
      }
    },
    [sections, onReorderSections],
  );

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
  }, []);

  const activeSection = activeId
    ? sections.find((s) => s.id === activeId)
    : null;
  const activeSectionIndex = activeSection
    ? sections.findIndex((s) => s.id === activeId)
    : -1;

  if (isCollapsed) {
    return (
      <div className="w-12 bg-gray-50 border-r flex flex-col items-center py-4 gap-2">
        {sections.map((_, index) => (
          <button
            key={index}
            onClick={() => onSectionClick(index)}
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
              index === currentIndex
                ? "bg-primary-500 text-white"
                : "bg-white border hover:bg-gray-100"
            }`}
          >
            {index + 1}
          </button>
        ))}
        <button
          onClick={() => onAddSection()}
          disabled={isAddingSection}
          className="w-8 h-8 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center hover:border-primary-500 hover:text-primary-500 transition-colors disabled:opacity-50"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="w-72 bg-gray-50 border-r flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b bg-white">
        <h3 className="font-semibold text-gray-900">Secciones</h3>
        <p className="text-xs text-gray-500 mt-1">
          {sections.length} {sections.length === 1 ? "sección" : "secciones"}
          {isReordering && " • Guardando..."}
        </p>
      </div>

      {/* Section List with DnD */}
      <div className="flex-1 overflow-y-auto p-2">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <SortableContext
            items={sections.map((s) => s.id)}
            strategy={verticalListSortingStrategy}
          >
            {sections.map((section, index) => (
              <SortableSectionItem
                key={section.id}
                section={section}
                index={index}
                isActive={index === currentIndex}
                onSectionClick={() => onSectionClick(index)}
                onDeleteSection={() => onDeleteSection(section.id)}
              />
            ))}
          </SortableContext>

          <DragOverlay>
            {activeSection ? (
              <DragOverlaySectionItem
                section={activeSection}
                index={activeSectionIndex}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>

      {/* Add Section Buttons */}
      <div className="p-3 border-t bg-white flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onAddSection()}
          disabled={isAddingSection}
          className="flex-1"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nueva Sección
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              disabled={isAddingSection}
              title="Opciones de creación"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onAddSection()}>
              <Plus className="h-4 w-4 mr-2" />
              Sección manual
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onAddSection({ withAI: true })}>
              <Sparkles className="h-4 w-4 mr-2 text-purple-600" />
              Generar con IA
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
